import React, {FC, useEffect} from 'react'
import {useAppDispatch} from 'redux/config'
import {
  Button,
  Select,
  Stack,
  Switch,
  TextArea,
  Tooltip,
  LabelField,
  Typography,
  Icon,
  TextField,
} from '@mdsol/onex-design'
import {databasesAC, fetchConfigurationOptions, submitBulkEditRequest} from 'redux/databases/databases.duck'
import {useSelector} from 'react-redux'
import {select_bulkEdit} from 'redux/databases/selectors'
import {select_auth_user} from 'redux/auth/selectors'
import {UrlSelection} from 'commonTypes'
import {ConfigurationOptionType} from 'graphql-api'
import {thunkWithSpeedBump} from 'redux/speedBump/speedBump.duck'
import {SpeedBumpType} from 'redux/speedBump/state'
import {useRouter} from '@uirouter/react'

const DatabaseBulkEditPanel: FC = () => {
  const dispatch = useAppDispatch()
  const router = useRouter()

  useEffect(() => {
    dispatch(fetchConfigurationOptions())
  }, [dispatch])

  const {
    configurationOptions,
    configurationOption,
    urlOption,
    configurationEnabled,
    submitButtonEnabled,
    specifiedUrls,
    defaultMenuIsOpen,
  } = useSelector(select_bulkEdit)

  const urlOptions = [
    {
      value: UrlSelection.ALL,
      label: 'All',
    },
    {
      value: UrlSelection.MULTIPLE,
      label: 'Specify Urls',
    },
  ]

  const {isAdmin} = useSelector(select_auth_user)
  
  const handleConfigurationOptionSelection = option => {
    dispatch(databasesAC.configurationOptionSelectionApplied(option))
  }

  const handleUrlSelection = option => {
    dispatch(databasesAC.urlSelectionApplied(option))
  }

  const handleSpecifyUrlsAdd = (text) => {
    dispatch(databasesAC.specifyUrlsApplied(text))
  }

  const toggleConfigurationEnabled = () => {
    dispatch(databasesAC.configurationEnabledApplied(!configurationEnabled))
  }

  const handleTextvalueChanged = (text) => {
    dispatch(databasesAC.configurationValueApplied(text))
  }

  const handleNumbervalueChanged = (event) => {
    const regEx = /^[0-9]+$/
    if(regEx.test(event.target.value)) {
      dispatch(databasesAC.configurationValueApplied(event.target.value))
    } else {
      dispatch(databasesAC.configurationValueApplied(''))
    }    
  }

  const handleBulkEdit = () => {
    if(urlOption?.value === UrlSelection.ALL) {
      dispatch(thunkWithSpeedBump(() => submitBulkEditRequest(router), SpeedBumpType.bulkEditForAllUrls))
    } else {
      dispatch(submitBulkEditRequest(router))
    }
  }

  const handleClose = () => {
    dispatch(databasesAC.hideBulkEditPanel())
    dispatch(databasesAC.clearBulkEditForm())
  }

  return (
    <div className='row-details-panel' role='database-details-container'>
      <div className='header onex-data-grid-control'>
        <Typography variant='h4'>
          Bulk Edit
        </Typography>

        <Button dataTestId='close-details-icon' onClick={handleClose} variant='secondary' buttonStyle='ghost' buttonType='icon'>
          <Icon>close</Icon>
        </Button>
      </div>
      
      <Stack className='permanent-dropdowns' key='permanent-ddls'>
        <Select
          key='database-selector'
          className='dynamic-ddl'
          dataTestId='select-database'
          options={urlOptions}
          onSelect={handleUrlSelection}
          value={urlOption}
          label='Url'
          labelFieldId='Database Label Id'
          informativeText='Select all urls or specify list of urls'
          menuPosition='fixed'
          size='sm'
          defaultMenuIsOpen={defaultMenuIsOpen}
          required
        />
        {urlOption !== null && urlOption.value === UrlSelection.MULTIPLE &&
          <div className='custom-url-textarea'>
            <TextArea
              className='custom-textarea'
              dataTestId='specify-urls-id'
              id='custom-textarea-specify-urls-id'
              label='Specify Urls'
              size='sm'
              rows={10}
              informativeText='Enter Urls separated by comma, new line or space in the format: abc.mdsol.com'
              placeholder='Enter Urls separated by comma, new line or space in the format: abc.mdsol.com'
              onChange={handleSpecifyUrlsAdd}
              value={specifiedUrls}
            />
          </div>
        }
        <Select
          key='configuration-options-selector'
          className='dynamic-ddl'
          dataTestId='select-configuration-option'
          groupedOptions={configurationOptions}
          onSelect={handleConfigurationOptionSelection}
          value={configurationOption}
          label='Configuration Tag'
          labelFieldId='Configuration Tag Label Id'
          menuPosition='fixed'
          informativeText='Select Configuration Tag'
          size='sm'
          defaultMenuIsOpen={defaultMenuIsOpen}
          required
        />
        {configurationOption !== null &&
          <div className='flex-container'>
            <div>
              <LabelField
                className='custom-label-field'
                dataTestId='label-value-id'
                isRequired
              >
                Value
              </LabelField>
            </div>
            {configurationOption?.type === ConfigurationOptionType.Boolean &&
              <div>
                <Tooltip
                  id='toggle-enabled-tooltip'
                  tooltipChildren={configurationEnabled ? 'Disable' : 'Enable'}
                  placement='bottom-end'
                >
                  <Switch
                    checked={configurationEnabled}
                    onChange={toggleConfigurationEnabled}
                    id={'enabled-value-id'}
                    dataTestId='value-enabled'
                    className='is-enabled'
                  />
                </Tooltip>
              </div>
            }            
            {configurationOption?.type === ConfigurationOptionType.Integer &&
              <div>
                <Stack>
                  <input
                    className='number-input'
                    id='text-textfield-custom-id'
                    type='number'
                    onChange={handleNumbervalueChanged}
                    min={0}
                  />
                </Stack>              
              </div>
            } 
            {(configurationOption?.type === ConfigurationOptionType.String || 
              configurationOption?.type === ConfigurationOptionType.TimeSpan) &&
              <div>
                <Stack>
                  <TextField
                    className='custom-textfield'
                    dataTestId='text-textfield-custom-test-id'
                    id='text-textfield-custom-id'
                    showClearBtn
                    size='sm'
                    onChange={handleTextvalueChanged}
                  />
                </Stack>                  
              </div>
            }            
          </div>        
        }
        
        <div className='submit-button-container'>
          <Button
            key='submit-button'
            dataTestId='submit-button'
            onClick={handleBulkEdit}
            size='sm'
            disabled={!(isAdmin && submitButtonEnabled)}
          >
            Submit
          </Button>
        </div>
      </Stack>
      <div className='footer onex-data-grid-control'>
        <Stack direction='horizontal' gap={2}>
          <Button dataTestId='close-details-button' onClick={handleClose} size='md' variant='secondary'>
            Close
          </Button>
        </Stack>
      </div>
    </div>
  )
}

export default DatabaseBulkEditPanel
